import { countriesList } from '@cmg/common';
import { Section, SectionContentSkeleton, Stack, Typography } from '@cmg/design-system';

import { useGetOfferingCountryCode } from '../../../../../common/hooks/useGetOfferingCountryCode';
import { isInvestorUnlinked } from '../../../../../common/util/rolodex/unlinked-investor.util';
import { OfferingType } from '../../../../../graphql';
import {
  OrderBookPersonaType,
  useIdentifyOrderBookPersona,
} from '../../hooks/useIdentifyOrderBookPersona';
import { useComplianceContext } from '../context/ComplianceContext';
import CertificatesNoAccessBanner from './components/CertificatesNoAccessBanner';
import CertificateTable from './components/CertificateTable';
import ComplianceActionRequired from './components/ComplianceActionRequired';
import ComplianceInfo from './components/ComplianceInfo';

export type Props = {
  offeringId: string;
  cmgEntityKey?: string;
};

const ComplianceCertificates: React.FC<Props> = ({ offeringId, cmgEntityKey }) => {
  const { canRead, attestation, attestationForm, loading, offeringType } =
    useComplianceContext().certificates;
  const { type } = useIdentifyOrderBookPersona({ offeringId });
  const offeringCountryCode = useGetOfferingCountryCode(offeringId);
  const isSellSide = type !== OrderBookPersonaType.BUY_SIDE;

  if (loading) {
    return (
      <Section title="Certificates">
        <SectionContentSkeleton />
      </Section>
    );
  }

  const isUnlinkedInvestorWarningVisible =
    isInvestorUnlinked(cmgEntityKey) && offeringType === OfferingType.Ipo;

  const isUsOffering = offeringCountryCode === countriesList.Usa.countryCode;

  const isCertificatesVisibleForOffering =
    (offeringType === OfferingType.Ipo || offeringType === OfferingType.Convertible) &&
    isUsOffering;

  if (!canRead && isSellSide) {
    return isUnlinkedInvestorWarningVisible ? <ComplianceActionRequired /> : null;
  }

  return (
    <Stack spacing={1}>
      <ComplianceActionRequired />

      {isSellSide && isCertificatesVisibleForOffering && <CertificatesNoAccessBanner />}

      <Section title="Certificates">
        <Stack gap={1}>
          <ComplianceInfo
            offeringId={offeringId}
            isCertificatesVisibleForOffering={isCertificatesVisibleForOffering}
          />

          {isCertificatesVisibleForOffering && <Typography variant="h4">Current Status</Typography>}

          <CertificateTable
            attestation={attestation}
            attestationForm={attestationForm}
            isSellSide={isSellSide}
            isCertificatesVisibleForOffering={isCertificatesVisibleForOffering}
          />
        </Stack>
      </Section>
    </Stack>
  );
};

export default ComplianceCertificates;
